import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";

/**
 * Text-based date input in Italian format (gg/mm/aaaa or gg/mm/aa).
 * - Accepts 2-digit year: 26 -> 2026, 45 -> 2045, 46 -> 1946 (30-year cutoff).
 * - Emits ISO YYYY-MM-DD via onChange(iso).
 * - Value prop is expected in ISO format (YYYY-MM-DD).
 */

// Two-digit-year expansion using a 30-year cutoff:
//   00..(currentYY+30) -> 2000..    ; else -> 1900..
function expandYY(yy) {
  const y = Number(yy);
  if (!Number.isFinite(y)) return null;
  const nowYY = new Date().getFullYear() % 100;
  const cutoff = (nowYY + 30) % 100;
  return y <= cutoff ? 2000 + y : 1900 + y;
}

// ISO -> "gg/mm/aaaa"
function isoToIt(iso) {
  if (!iso || typeof iso !== "string") return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

// "gg/mm/aa" or "gg/mm/aaaa" -> ISO YYYY-MM-DD (or "" if invalid/partial)
export function parseItToIso(text) {
  if (!text) return "";
  // strip anything not digit or /
  const s = String(text).replace(/[^\d/]/g, "");
  const parts = s.split("/").filter(Boolean);
  if (parts.length < 3) return "";
  let [dd, mm, yy] = parts;
  const d = Number(dd), mo = Number(mm);
  if (!Number.isFinite(d) || !Number.isFinite(mo)) return "";
  let year = yy.length === 2 ? expandYY(yy) : Number(yy);
  if (!Number.isFinite(year)) return "";
  if (d < 1 || d > 31 || mo < 1 || mo > 12) return "";
  const iso = `${String(year).padStart(4, "0")}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const test = new Date(iso);
  if (Number.isNaN(test.getTime())) return "";
  return iso;
}

export default function SmartDateInput({
  value = "",
  onChange,
  disabled = false,
  required = false,
  "data-testid": testId,
  className = "",
}) {
  const [text, setText] = useState(isoToIt(value));

  useEffect(() => { setText(isoToIt(value)); }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value;
    // Project to digits only, then reformat as gg/mm/aaaa. This works both
    // for pure digit typing ('150326' → '15/03/26') and when the user has
    // slashes already (they can freely add/remove and we re-normalise).
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    let out = digits;
    if (digits.length > 4) out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) out = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    setText(out);
  };

  const commit = (raw) => {
    const iso = parseItToIso(raw);
    if (iso) {
      setText(isoToIt(iso));
      onChange?.(iso);
    } else if (raw === "") {
      onChange?.("");
    }
  };

  return (
    <Input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder="gg/mm/aa"
      value={text}
      disabled={disabled}
      required={required}
      data-testid={testId}
      className={className}
      onChange={handleChange}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); commit(e.currentTarget.value); e.currentTarget.blur(); }
      }}
    />
  );
}
